/**
 * File-related constants shared across apps.
 *
 * @module @f2e/shared/constants/file
 */
/**
 * Maximum file size in MB for uploads.
 * Must match backend MAX_FILE_SIZE_MB in codeer/common/files.py
 */
export const MAX_FILE_SIZE_MB = 50;
/**
 * Maximum file size in bytes for uploads.
 */
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
/**
 * Tabular source types are capability-gated by the backend. Keep these
 * separate from the general document list so a disabled rollout does not
 * change the existing Knowledge Base upload behavior.
 */
export const TABULAR_FILE_EXTENSIONS = [".csv", ".xlsx"];
export const TABULAR_MIME_TYPES = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
export const GOOGLE_SHEETS_MIME_TYPE = "application/vnd.google-apps.spreadsheet";
// ============================================================================
// Supported MIME Types
// Must match backend ALLOWED_CONTENT_TYPES in codeer/common/files.py
// ============================================================================
/**
 * Document MIME types (excluding images).
 * Use this for flows that intentionally accept documents but not images.
 */
export const SUPPORTED_DOCUMENT_MIME_TYPES = [
    // PDF
    "application/pdf",
    // Text formats
    "text/plain",
    "text/markdown",
    "text/html",
    // Microsoft Office
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    // Google Workspace
    "application/vnd.google-apps.document",
    "application/vnd.google-apps.presentation",
];
/**
 * Image MIME types supported for upload.
 */
export const SUPPORTED_IMAGE_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
];
/**
 * Audio MIME types supported for upload.
 */
export const SUPPORTED_AUDIO_MIME_TYPES = [
    "audio/mpeg",
    "audio/mp4",
    "audio/wav",
    "audio/ogg",
    "audio/webm",
    "audio/x-m4a",
];
/**
 * General supported MIME types for uploads backed by the markdown / knowledge
 * ingestion pipeline (documents + images).
 *
 * Note: Backend also accepts any `text/*` subtype, handled by the wildcard below.
 */
export const SUPPORTED_MIME_TYPES = [
    ...SUPPORTED_DOCUMENT_MIME_TYPES,
    ...SUPPORTED_IMAGE_MIME_TYPES,
    // Wildcard: Accept any text/* subtype (matches backend behavior)
    "text/*",
];
/**
 * Attachment MIME types supported for chat/copilot uploads.
 */
export const SUPPORTED_ATTACHMENT_MIME_TYPES = [
    ...SUPPORTED_MIME_TYPES,
    ...SUPPORTED_AUDIO_MIME_TYPES,
];
// Form answers are stored as files, not sent to the chat ingestion pipeline.
export const FORM_FILE_MAX_SIZE_MB = 100;
export const FORM_FILE_MIME_TYPES = [
    ...SUPPORTED_ATTACHMENT_MIME_TYPES,
    ...TABULAR_MIME_TYPES,
    "audio/basic",
    "audio/m4a",
    "video/mp4",
    "video/webm",
    "video/quicktime",
    "video/mpeg",
    "video/x-msvideo",
];
export const FORM_FILE_EXTENSIONS = [
    ".pdf",
    ".txt",
    ".md",
    ".csv",
    ".docx",
    ".doc",
    ".pptx",
    ".xlsx",
    ".jpeg",
    ".jpg",
    ".png",
    ".gif",
    ".webp",
    ".html",
    ".mp3",
    ".mp4",
    ".m4a",
    ".wav",
    ".ogg",
    ".webm",
    ".mov",
    ".mpeg",
    ".mpg",
    ".avi",
];
/**
 * File extension workaround for HTML file input `accept` attribute.
 * Some browsers (especially macOS Safari) don't recognize markdown MIME type,
 * so we need to explicitly include the extension.
 */
export const MARKDOWN_FILE_EXTENSION = ".md";
/**
 * Builds the accept string for HTML file input elements.
 * Combines MIME types with necessary file extension workarounds.
 *
 * @param mimeTypes - Array of MIME types to accept
 * @param includeMarkdownExtension - Whether to include .md extension (default: true)
 * @returns Comma-separated string for the accept attribute
 *
 * @example
 * ```tsx
 * <input type="file" accept={buildFileAcceptString(SUPPORTED_MIME_TYPES)} />
 * // Result: "application/pdf,text/plain,...,.md"
 * ```
 */
export const buildFileAcceptString = (mimeTypes, includeMarkdownExtension = true) => {
    const types = [...mimeTypes];
    if (includeMarkdownExtension && !types.includes(MARKDOWN_FILE_EXTENSION)) {
        types.push(MARKDOWN_FILE_EXTENSION);
    }
    return types.join(",");
};
